import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { loadProducts, loadConfig, loadJob, loadAccounts } from "src/redux/actions/shopify";
import Alert from "src/components/Alert";
import ServerPaginationTable from "src/components/ServerPaginationTable";
import SyncStatus from "src/components/SyncStatus";
import getSymbolFromCurrency from "currency-symbol-map";
import { migrateShopifyData, migrateShopifyDataForProducts } from "src/api";
import { addToast } from "src/redux/actions/toasts";
import { getQueryParams } from "src/utils/Utility";

function Products() {
  const [syncStatus, setSyncStatus] = useState("enabled");
  const products = useSelector((state) => state.shopify.products);
  const dispatch = useDispatch();
  const job = useSelector((state) => state.shopify.job);
  const [pageCount, setPageCount] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [status, setStatus] = useState([]);
  const [sortBy, setSortBy] = useState();
  const [loading, setLoading] = useState(false);
  const accounts = useSelector((state) => state.shopify.accounts);

  useEffect(() => {
    dispatch(loadConfig());
    if (accounts?.data?.length === 0) {
      dispatch(loadAccounts());
    }
  }, []);

  useEffect(() => {
    dispatch(loadJob());
  }, job);

  const data = useMemo(() => products.data, [products]);
  useEffect(() => {
    setLoading(false);
    if (data && data.totalPages) {
      setPageCount(data.totalPages);
    }
  }, [data]);

  const columns = useMemo(
    () => [
      {
        Header: "Product ID",
        accessor: "cartProductId",
      },
      {
        Header: "Variant ID",
        accessor: "productVariantId",
      },
      {
        Header: "SKU",
        accessor: "sku",
      },
      {
        Header: "Name",
        accessor: "productName",
        Cell: ({ cell: { row } }) => (
          <span>
            {row.original.productName}{" "}
            {row.original.productVariantName &&
              row.original.productVariantName !== "Default Title" &&
              "(" + row.original.productVariantName + ")"}
          </span>
        ),
      },
      {
        Header: "Unit Price",
        accessor: "productUnitPrice",
        Cell: ({ cell: { row } }) => (
          <span>
            {row.original.productUnitPrice && getSymbolFromCurrency(row.original.currency)}{" "}
            {row.original.productUnitPrice}
          </span>
        ),
        className: "text-right",
      },
      {
        Header: "Initial Quantity",
        accessor: "openingQuantity",
        className: "text-right",
      },
      {
        Header: "Cost Per Item",
        accessor: "costPerItem",
        Cell: ({ cell: { row } }) => (
          <span>
            {getSymbolFromCurrency(row.original.currency)}{" "}
            {row.original.costPerItem}
          </span>
        ),
        className: "text-right",
      },
      {
        Header: "Shopify Store",
        accessor: "shopifyStore",
        Cell: ({ cell: { row } }) => {
          const account =
            row.original.cartAccountId &&
            accounts?.data?.find((item) => item.id === row.original.cartAccountId);
          return account?.name || "";
        },
      },
      {
        Header: "Sync Status",
        accessor: "syncStatus",
        Cell: ({ cell: { row } }) => {
          return (
            <SyncStatus
              status={row.original.syncStatus}
              reason={row.original.reason}
              id={row.original.cartProductId}
            />
          );
        },
        disableGlobalFilter: true,
        disableSortBy: true,
      },
      {
        Header: "Deskera Product Code",
        accessor: "deskeraDocumentCode",
      },
    ],
    [accounts]
  );

  const config = useSelector((state) => state.shopify.config);

  const refreshData = ({ pageSize, pageIndex, status, sortBy, search, query }) => {
    setPageSize(pageSize);
    setPageIndex(pageIndex);
    setStatus(status);
    setSortBy(sortBy);
    setLoading(true);

    const queryParams = getQueryParams(query);

    dispatch(loadProducts(pageSize, pageIndex, status, sortBy, search ?? "", queryParams));
  };

  async function handleSyncDataSubmit() {
    try {
      const resp = await migrateShopifyData();
    } catch (err) {
      dispatch(
        addToast({
          type: "danger",
          title: "Failure",
          message: err.message,
        })
      );
    }
  }

  return (
    <div className="p-4">
      <h3 className="mb-3">Products</h3>
      {(!config.data || !config.data.syncEnabled) && (
        <Alert type="warning">
          Your synced products will appear here. Please complete{" "}
          <Link to="/app/shopify" className="text-alert-warning">
            <u>Setup</u>
          </Link>{" "}
          before continuing.
        </Alert>
      )}
      <ServerPaginationTable
        data={data.content ? data.content : []}
        columns={columns}
        migrate={migrateShopifyDataForProducts}
        cart="shopify"
        refresh={refreshData}
        tab="Products"
        pageCount={pageCount}
        loading={loading}
        rowId="id"
      />
    </div>
  );
}

export default Products;
